/**
 * Batch 27h Lane B — Users tab on the SA Tenant detail page.
 *
 * Load-bearing UX (per ``backend/docs/batch_27h_acceptance_addendum.md``):
 *   * Verification status visible as a badge on every row — NOT a click-
 *     through to find out.
 *   * "Verify Email" button on the row itself for unverified users — NOT
 *     buried in a modal opened from a row click.
 *   * Confirm dialog with ONE click to confirm — NO email-typing friction.
 *   * Toast on success: ``User <email> verified — they can now log in``
 *     (NOT just "Verified") so the operator knows the next step works.
 *
 * SA user management (founder direction 2026-06-03):
 *   * "Edit" button → modal for name / email / password changes.
 *   * "Force password reset" button → confirmation modal → sends reset email.
 *   * "Clear lockout" button → immediate action, no confirmation (idempotent).
 */
import { useState, type ReactNode } from "react";
import {
  MailCheck,
  Pencil,
  KeyRound,
  Unlock,
  Send,
} from "lucide-react";
import {
  useClearLockout,
  useEditTenantUser,
  useForcePasswordReset,
  useResendVerification,
  useTenantUsers,
  useVerifyUser,
  type TenantUser,
} from "@/hooks/queries/admin/useAdminTenants";
import { Tooltip } from "@/components/common/Tooltip";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  tenantId: string;
  /**
   * Optional case-insensitive filter over name/email. Wired from the SA Demo
   * Detail Users card search box; omitted everywhere else (renders all rows).
   */
  searchQuery?: string;
}

/**
 * Icon row-action button — square hit target, tooltip + aria-label for an
 * accessible name (icon-only buttons MUST have one). `destructive` leans the
 * hover toward the status-rejected tone.
 */
function IconAction({
  icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}): JSX.Element {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-secondary)] disabled:opacity-50 ${
          destructive
            ? "hover:text-status-rejected-fg"
            : "hover:text-text-primary"
        }`}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

interface EditFormState {
  first_name: string;
  last_name: string;
  email: string;
}

export function TenantUsersTab({ tenantId, searchQuery }: Props): JSX.Element {
  const { data, isLoading, error } = useTenantUsers(tenantId);
  const verify = useVerifyUser(tenantId);
  const resend = useResendVerification(tenantId);
  const editUser = useEditTenantUser(tenantId);
  const forceReset = useForcePasswordReset(tenantId);
  const clearLockout = useClearLockout(tenantId);
  const addToast = useToastStore((s) => s.addToast);

  const [confirmTarget, setConfirmTarget] = useState<TenantUser | null>(null);
  const [editTarget, setEditTarget] = useState<TenantUser | null>(null);
  const [forceResetTarget, setForceResetTarget] = useState<TenantUser | null>(
    null,
  );
  const [editForm, setEditForm] = useState<EditFormState>({
    first_name: "",
    last_name: "",
    email: "",
  });

  if (isLoading) {
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Loading users…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        Couldn't load users: {error.message}
      </div>
    );
  }

  const allUsers = data?.users ?? [];
  const q = (searchQuery ?? "").trim().toLowerCase();
  const users = q
    ? allUsers.filter((u) => {
        const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase();
        return (
          name.includes(q) || (u.email ?? "").toLowerCase().includes(q)
        );
      })
    : allUsers;

  const handleResend = (u: TenantUser) => {
    resend.mutate(u.id, {
      onSuccess: (r) => {
        if (r.sent) {
          addToast(`Verification email re-sent to ${u.email}`, "success");
        } else {
          addToast(
            `Email send failed for ${u.email} — see Audit tab.`,
            "warning",
          );
        }
      },
      onError: (err) => {
        addToast(
          `Could not re-send: ${err.message || "unknown error"}`,
          "error",
        );
      },
    });
  };

  const handleVerify = (u: TenantUser) => {
    verify.mutate(u.id, {
      onSuccess: () => {
        addToast(
          `User ${u.email} verified — they can now log in.`,
          "success",
        );
        setConfirmTarget(null);
      },
      onError: (err) => {
        addToast(
          `Could not verify: ${err.message || "unknown error"}`,
          "error",
        );
      },
    });
  };

  const openEditModal = (u: TenantUser) => {
    setEditTarget(u);
    setEditForm({
      first_name: u.first_name ?? "",
      last_name: u.last_name ?? "",
      email: u.email ?? "",
    });
  };

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editTarget) return;

    const body: {
      first_name?: string;
      last_name?: string;
      email?: string;
    } = {};
    if (editForm.first_name.trim() !== (editTarget.first_name ?? "").trim())
      body.first_name = editForm.first_name.trim();
    if (editForm.last_name.trim() !== (editTarget.last_name ?? "").trim())
      body.last_name = editForm.last_name.trim();
    if (editForm.email.trim() !== (editTarget.email ?? "").trim())
      body.email = editForm.email.trim();

    if (Object.keys(body).length === 0) {
      addToast("No changes to save.", "warning");
      return;
    }

    editUser.mutate(
      { userId: editTarget.id, body },
      {
        onSuccess: (r) => {
          addToast(
            `Updated ${r.fields_updated.join(", ")} for ${editTarget.email}.`,
            "success",
          );
          setEditTarget(null);
        },
        onError: (err) => {
          if (err.error === "email_in_use") {
            addToast("That email is already in use by another user.", "error");
          } else {
            addToast(`Edit failed: ${err.message || "unknown error"}`, "error");
          }
        },
      },
    );
  };

  const handleForceReset = (u: TenantUser) => {
    forceReset.mutate(u.id, {
      onSuccess: () => {
        addToast(
          `Password reset email sent to ${u.email}. Account is locked until reset.`,
          "success",
        );
        setForceResetTarget(null);
      },
      onError: (err) => {
        addToast(
          `Force reset failed: ${err.message || "unknown error"}`,
          "error",
        );
        setForceResetTarget(null);
      },
    });
  };

  const handleClearLockout = (u: TenantUser) => {
    clearLockout.mutate(u.id, {
      onSuccess: () => {
        addToast(`Lockout cleared for ${u.email}.`, "success");
      },
      onError: (err) => {
        addToast(
          `Clear lockout failed: ${err.message || "unknown error"}`,
          "error",
        );
      },
    });
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-[var(--p-slate-150)] bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Verification</th>
              <th className="px-4 py-2 font-medium">Created</th>
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  {q
                    ? "No users match your search."
                    : "No users in this tenant yet."}
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const fullName =
                  `${u.first_name} ${u.last_name}`.trim() || u.email;
                return (
                  <tr
                    key={u.id}
                    className="border-t border-brand-100 transition-colors hover:bg-brand-50 dark:border-white/[0.04] dark:hover:bg-white/[0.03]"
                  >
                    <td className="px-4 py-2 font-medium text-text-primary dark:text-text-primary">
                      {fullName}
                    </td>
                    <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                      {u.email}
                    </td>
                    <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                      {u.role}
                    </td>
                    <td className="px-4 py-2">
                      {u.is_verified ? (
                        <span
                          aria-label="Verified"
                          className="inline-flex items-center gap-1 rounded-full bg-brand-positive/10 px-2 py-0.5 text-xs font-medium text-[var(--p-green-700)] ring-1 ring-brand-positive/30 dark:bg-brand-positive/15 dark:text-[var(--p-green-350)]"
                        >
                          <span aria-hidden>✓</span> verified
                        </span>
                      ) : (
                        <span
                          aria-label="Pending verification"
                          className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 ring-1 ring-yellow-300 dark:bg-yellow-500/10 dark:text-yellow-300 dark:ring-yellow-500/30"
                        >
                          <span aria-hidden>⏳</span> pending verification
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-0.5">
                        {/* Edit — always available */}
                        <IconAction
                          icon={<Pencil size={15} aria-hidden />}
                          label="Edit user"
                          onClick={() => openEditModal(u)}
                        />
                        {/* Force password reset */}
                        <IconAction
                          icon={<KeyRound size={15} aria-hidden />}
                          label="Force password reset"
                          onClick={() => setForceResetTarget(u)}
                          disabled={forceReset.isPending}
                          destructive
                        />
                        {/* Clear lockout — immediate, idempotent */}
                        <IconAction
                          icon={<Unlock size={15} aria-hidden />}
                          label="Clear lockout"
                          onClick={() => handleClearLockout(u)}
                          disabled={clearLockout.isPending}
                        />
                        {/* Verification actions — only for unverified */}
                        {!u.is_verified && (
                          <>
                            <IconAction
                              icon={<MailCheck size={15} aria-hidden />}
                              label="Verify email"
                              onClick={() => setConfirmTarget(u)}
                              disabled={verify.isPending}
                            />
                            <IconAction
                              icon={<Send size={15} aria-hidden />}
                              label="Resend verification email"
                              onClick={() => handleResend(u)}
                              disabled={resend.isPending}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Verify email confirmation dialog */}
      {confirmTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
            <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
              Verify {confirmTarget.email}?
            </h2>
            <p className="text-sm text-text-secondary dark:text-brand-100/70">
              This marks the user's email as verified and unblocks login
              immediately. Use this when the verification email failed
              delivery or the customer can't find it.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleVerify(confirmTarget)}
                disabled={verify.isPending}
                className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
              >
                {verify.isPending ? "Verifying…" : "Verify Email"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit user modal */}
      {editTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
            <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
              Edit {editTarget.email}
            </h2>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div>
                <label
                  htmlFor="edit-first-name"
                  className="block text-xs font-medium text-text-secondary dark:text-brand-100/70"
                >
                  First name
                </label>
                <input
                  id="edit-first-name"
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, first_name: e.target.value }))
                  }
                  className="mt-1 w-full rounded border border-brand-200 bg-white px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary dark:border-white/[0.08] dark:bg-surface-canvas dark:text-text-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="edit-last-name"
                  className="block text-xs font-medium text-text-secondary dark:text-brand-100/70"
                >
                  Last name
                </label>
                <input
                  id="edit-last-name"
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, last_name: e.target.value }))
                  }
                  className="mt-1 w-full rounded border border-brand-200 bg-white px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary dark:border-white/[0.08] dark:bg-surface-canvas dark:text-text-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="edit-email"
                  className="block text-xs font-medium text-text-secondary dark:text-brand-100/70"
                >
                  Email
                </label>
                <input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, email: e.target.value }))
                  }
                  className="mt-1 w-full rounded border border-brand-200 bg-white px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary dark:border-white/[0.08] dark:bg-surface-canvas dark:text-text-primary"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editUser.isPending}
                  className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
                >
                  {editUser.isPending ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Force password reset confirmation dialog */}
      {forceResetTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
            <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
              Force password reset for {forceResetTarget.email}?
            </h2>
            <p className="text-sm text-text-secondary dark:text-brand-100/70">
              This locks the account and sends a password reset email. The user
              will not be able to log in until they reset their password.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setForceResetTarget(null)}
                className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleForceReset(forceResetTarget)}
                disabled={forceReset.isPending}
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {forceReset.isPending ? "Sending…" : "Force reset"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
